import {Params} from '@angular/router';
import {FILTRO_VUOTO, SinotticoFiltro, SinotticoOrdinamento} from '../_models/SinotticoFiltro';
import {SinotticoRisorsa} from '../_models/SinotticoRisorsa';
import {SINOTTICO_STATI_ANOMALI, SinotticoStato} from '../_models/SinotticoStato';

/**
 * Il predicato di filtro è **uno solo**, vive sul client (FR-025) e si applica in **due momenti**:
 * alla presentazione dell'insieme caricato e all'arrivo di ogni messaggio (FR-028).
 *
 * ⛔ Un cambio di filtro **non deve produrre alcuna richiesta** (SC-003, SC-019): l'endpoint non ha
 * parametri di filtro e non va richiamato. Se qualcuno «risolve» un filtro con una rifetch, la
 * feature perde la proprietà per cui esiste.
 */
export function superaFiltro(risorsa: SinotticoRisorsa, filtro: SinotticoFiltro): boolean {
  return corrispondeReparto(risorsa, filtro)
    && (filtro.tipoRisorsa === 0 || risorsa.tipoRisorsa === filtro.tipoRisorsa)
    && corrispondeStato(risorsa, filtro)
    && corrispondeOperatore(risorsa, filtro)
    && corrispondeLinea(risorsa, filtro)
    && corrispondeRicerca(risorsa, filtro)
    && (!filtro.soloConOrdini || haOrdiniAssegnati(risorsa));
}

/**
 * Quanti criteri **riducono** l'insieme mostrato. È il numero sul bottone «Filtri»: da quando la
 * barra è chiusa in un pannello, senza quel numero un filtro attivo diventa **invisibile** — e una
 * griglia con metà delle macchine sarebbe indistinguibile da mezzo reparto spento.
 *
 * ⚠️ Non conta gli **stati**, che restano visibili come pill fuori dal pannello e si vedono da sé,
 * né l'**ordinamento**, che non nasconde nulla.
 */
export function contaFiltriAttivi(filtro: SinotticoFiltro): number {
  return [
    filtro.reparti.length > 0,
    filtro.tipoRisorsa !== FILTRO_VUOTO.tipoRisorsa,
    !!filtro.operatore?.trim(),
    !!filtro.linea?.trim(),
    !!filtro.ricerca?.trim(),
    filtro.soloAnomalie,
    filtro.soloConOrdini,
  ].filter(Boolean).length;
}

export function applicaFiltro(risorse: SinotticoRisorsa[], filtro: SinotticoFiltro): SinotticoRisorsa[] {
  return ordina(risorse.filter(r => superaFiltro(r, filtro)), filtro);
}

export function ordina(risorse: SinotticoRisorsa[], filtro: SinotticoFiltro): SinotticoRisorsa[] {
  const verso = filtro.ordinamentoAsc ? 1 : -1;
  return [...risorse].sort((a, b) => verso * confronta(a, b, filtro.ordinamento));
}

/**
 * I contatori delle pill si calcolano sull'insieme **completo**, non sul sottoinsieme filtrato:
 * sono un riepilogo di *tutti* gli stati, compresi quelli attualmente esclusi dal filtro (FR-027).
 * È l'altra metà della ragione per cui il server non pre-filtra.
 */
export function contatoriPerStato(risorse: SinotticoRisorsa[]): Record<SinotticoStato, number> {
  const contatori = {} as Record<SinotticoStato, number>;
  [SinotticoStato.Ferma, SinotticoStato.InLavorazione, SinotticoStato.InPausa,
    SinotticoStato.Sospeso, SinotticoStato.Terminato, SinotticoStato.Allarme]
    .forEach(stato => contatori[stato] = 0);
  risorse.forEach(r => contatori[r.statoSinottico] = (contatori[r.statoSinottico] ?? 0) + 1);
  return contatori;
}

/**
 * I filtri vivono nella **query string della pagina** (FR-029), che è già il meccanismo con cui il
 * monitor a parete si apre da bookmark (SC-011). Un criterio non riconosciuto viene **ignorato**,
 * gli altri restano applicati, e in nessun caso parte una richiesta al server.
 */
export function filtroDaQueryString(params: Params): SinotticoFiltro {
  return {
    ...FILTRO_VUOTO,
    reparti: elenco(params['reparti']),
    tipoRisorsa: intero(params['tipoRisorsa'], 0),
    stati: elenco(params['stati']).map(s => intero(s, -1)).filter(s => s >= 0),
    operatore: testo(params['operatore']),
    linea: testo(params['linea']),
    ricerca: testo(params['ricerca']),
    soloAnomalie: params['soloAnomalie'] === 'true',
    soloConOrdini: params['soloConOrdini'] === 'true',
    ordinamento: ordinamentoValido(params['ordinamento']),
    ordinamentoAsc: params['ordinamentoAsc'] !== 'false',
    vista: params['vista'] === 'table' ? 'table' : 'grid',
    mostraTempi: params['mostraTempi'] !== 'false',
  };
}

/** Solo i criteri diversi dal predefinito finiscono nell'URL: un bookmark leggibile è un bookmark usato. */
export function queryStringDaFiltro(filtro: SinotticoFiltro): Params {
  const params: Params = {};
  if (filtro.reparti.length) { params['reparti'] = filtro.reparti.join(','); }
  if (filtro.tipoRisorsa) { params['tipoRisorsa'] = `${filtro.tipoRisorsa}`; }
  if (filtro.stati.length) { params['stati'] = filtro.stati.join(','); }
  if (filtro.operatore) { params['operatore'] = filtro.operatore; }
  if (filtro.linea) { params['linea'] = filtro.linea; }
  if (filtro.ricerca) { params['ricerca'] = filtro.ricerca; }
  if (filtro.soloAnomalie) { params['soloAnomalie'] = 'true'; }
  if (filtro.soloConOrdini) { params['soloConOrdini'] = 'true'; }
  if (filtro.ordinamento !== 'codice') { params['ordinamento'] = filtro.ordinamento; }
  if (!filtro.ordinamentoAsc) { params['ordinamentoAsc'] = 'false'; }
  if (filtro.vista !== 'grid') { params['vista'] = filtro.vista; }
  if (!filtro.mostraTempi) { params['mostraTempi'] = 'false'; }
  return params;
}

// ----------------------------------------------------------------------------------------------

function haOrdiniAssegnati(risorsa: SinotticoRisorsa): boolean {
  return risorsa.odlCoda > 0 || risorsa.odlStart > 0 || risorsa.odlPause > 0;
}

function corrispondeReparto(risorsa: SinotticoRisorsa, filtro: SinotticoFiltro): boolean {
  return !filtro.reparti.length || filtro.reparti.includes(risorsa.reparto ?? '');
}

function corrispondeStato(risorsa: SinotticoRisorsa, filtro: SinotticoFiltro): boolean {
  if (filtro.soloAnomalie && !SINOTTICO_STATI_ANOMALI.includes(risorsa.statoSinottico)) {
    return false;
  }
  return !filtro.stati.length || filtro.stati.includes(risorsa.statoSinottico);
}

function corrispondeOperatore(risorsa: SinotticoRisorsa, filtro: SinotticoFiltro): boolean {
  return !filtro.operatore || contiene(risorsa.operatore, filtro.operatore);
}

function corrispondeLinea(risorsa: SinotticoRisorsa, filtro: SinotticoFiltro): boolean {
  if (!filtro.linea) {
    return true;
  }
  return contiene(risorsa.classe, filtro.linea) || `${risorsa.lineaNum ?? ''}` === filtro.linea.trim();
}

function corrispondeRicerca(risorsa: SinotticoRisorsa, filtro: SinotticoFiltro): boolean {
  if (!filtro.ricerca?.trim()) {
    return true;
  }
  const ordine = risorsa.ordineAttivo;
  // Anche l'articolo del secondo ordine (feature 147, FR-037): dopo il lancio del prossimo ordine,
  // chi cerca l'articolo in produzione deve trovare ancora la macchina che lo produce.
  const secondario = risorsa.ordineSecondario;
  return [risorsa.codice, risorsa.descrizione, ordine?.codiceArticolo, ordine?.descArticolo,
    ordine?.commessa, ordine?.rifOdP, ordine?.ragSocialeCliente, secondario?.codiceArticolo,
    secondario?.descArticolo]
    .some(campo => contiene(campo, filtro.ricerca));
}

function contiene(campo: string | null | undefined, cercato: string): boolean {
  return (campo ?? '').toLowerCase().includes(cercato.trim().toLowerCase());
}

function confronta(a: SinotticoRisorsa, b: SinotticoRisorsa, criterio: SinotticoOrdinamento): number {
  switch (criterio) {
    case 'stato':
      return a.statoSinottico - b.statoSinottico || a.codice.localeCompare(b.codice);
    case 'produzione':
      return a.produzioneOraria - b.produzioneOraria || a.codice.localeCompare(b.codice);
    case 'fermo':
      return chiaveFermo(a) - chiaveFermo(b) || a.codice.localeCompare(b.codice);
    default:
      return a.codice.localeCompare(b.codice);
  }
}

/**
 * La chiave di «per fermo» cresce con la **durata** del fermo, come prima che `fermaDaUnix`
 * diventasse un istante (feature 147, research R10 punto 2): in crescente prima le risorse senza
 * dato, poi quella ferma da meno tempo. Confrontare l'istante così com'è invertirebbe l'ordine.
 */
function chiaveFermo(risorsa: SinotticoRisorsa): number {
  return risorsa.fermaDaUnix == null ? -1 : Number.MAX_SAFE_INTEGER - risorsa.fermaDaUnix;
}

function elenco(valore: unknown): string[] {
  return typeof valore === 'string' && valore.length
    ? valore.split(',').map(v => v.trim()).filter(v => v.length > 0)
    : [];
}

function intero(valore: unknown, predefinito: number): number {
  const numero = Number.parseInt(`${valore ?? ''}`, 10);
  return Number.isFinite(numero) ? numero : predefinito;
}

function testo(valore: unknown): string {
  return typeof valore === 'string' ? valore : '';
}

function ordinamentoValido(valore: unknown): SinotticoOrdinamento {
  const ammessi: SinotticoOrdinamento[] = ['codice', 'stato', 'produzione', 'fermo'];
  return ammessi.includes(valore as SinotticoOrdinamento) ? valore as SinotticoOrdinamento : 'codice';
}
